import {
  DASHBOARD_WIDGETS,
  getDefaultDashboardLayout,
  getDashboardWidgetById,
} from '@/components/Dashboard/widgets/dashboardWidgetRegistry';

const BASE_STORAGE_KEY = 'besafer.dashboard.layout';
const ROW_LOCK_ORDER_SLOTS = {
  first: [1, 2],
  second: [3, 4],
};

function sanitizeStoragePart(value) {
  return String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_');
}

function getStorageKey(storageContext = {}) {
  const userId = sanitizeStoragePart(storageContext.userId);
  const companyCode = sanitizeStoragePart(storageContext.companyCode);
  const parts = [BASE_STORAGE_KEY];

  if (userId) {
    parts.push(userId);
  }

  if (companyCode) {
    parts.push(companyCode);
  }

  return parts.join('.');
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function getRegistryOrder(widgetId) {
  const widget = getDashboardWidgetById(widgetId);

  return widget ? widget.defaultOrder : Number.MAX_SAFE_INTEGER;
}

function getWidgetRowLock(widgetId) {
  const widget = getDashboardWidgetById(widgetId);

  return widget ? widget.rowLock : null;
}

function getLayoutWidgetRowLock(widget) {
  return widget?.enabled === false
    ? null
    : getWidgetRowLock(widget.id);
}

function getRowLockOrderSlots(rowLock, widgetsCount) {
  if (widgetsCount <= 2) {
    return [1, 2].slice(0, widgetsCount);
  }

  if (rowLock === 'first') {
    return widgetsCount >= 2 ? [1, 2] : [1];
  }

  if (rowLock === 'second') {
    return widgetsCount === 3 ? [2, 3] : ROW_LOCK_ORDER_SLOTS.second;
  }

  return ROW_LOCK_ORDER_SLOTS[rowLock] || null;
}

function getClosestAvailableSlot(
  currentSlot,
  allowedSlots,
  usedSlots
) {
  return allowedSlots
    .filter((slot) => !usedSlots.has(slot))
    .sort((firstSlot, secondSlot) => {
      const firstDistance = Math.abs(firstSlot - currentSlot);
      const secondDistance = Math.abs(secondSlot - currentSlot);

      if (firstDistance === secondDistance) {
        return firstSlot - secondSlot;
      }

      return firstDistance - secondDistance;
    })[0];
}

function normalizeLockedRows(
  layout = [],
  preferredWidgetId = null,
  preferredOrderSlot = null
) {
  const orderedLayout = [...layout].sort(
    (firstWidget, secondWidget) => {
      if (firstWidget.order === secondWidget.order) {
        return (
          getRegistryOrder(firstWidget.id) -
          getRegistryOrder(secondWidget.id)
        );
      }

      return firstWidget.order - secondWidget.order;
    }
  );
  const activeOrderedLayout = orderedLayout.filter(
    (widget) => widget.enabled !== false
  );
  const inactiveOrderedLayout = orderedLayout.filter(
    (widget) => widget.enabled === false
  );
  const normalizedSlots = [];
  const usedSlots = new Set();
  const preferredWidgetIndex = activeOrderedLayout.findIndex(
    (widget) =>
      widget.id === preferredWidgetId &&
      !getLayoutWidgetRowLock(widget)
  );
  const parsedPreferredOrderSlot = Number(preferredOrderSlot);
  let preferredSlot = null;

  if (preferredWidgetIndex >= 0) {
    preferredSlot = Number.isFinite(parsedPreferredOrderSlot)
      ? Math.min(
          activeOrderedLayout.length,
          Math.max(1, Math.round(parsedPreferredOrderSlot))
        )
      : preferredWidgetIndex + 1;
  }

  if (preferredSlot) {
    usedSlots.add(preferredSlot);
  }

  activeOrderedLayout.forEach((widget, index) => {
    const allowedSlots = getRowLockOrderSlots(
      getLayoutWidgetRowLock(widget),
      activeOrderedLayout.length
    );

    if (!allowedSlots) {
      return;
    }

    const currentSlot = index + 1;
    const isCurrentSlotAllowed =
      allowedSlots.includes(currentSlot) &&
      !usedSlots.has(currentSlot);
    const targetSlot = isCurrentSlotAllowed
      ? currentSlot
      : getClosestAvailableSlot(currentSlot, allowedSlots, usedSlots);

    if (!targetSlot) {
      return;
    }

    normalizedSlots[targetSlot - 1] = widget;
    usedSlots.add(targetSlot);
  });

  if (preferredSlot) {
    normalizedSlots[preferredSlot - 1] =
      activeOrderedLayout[preferredWidgetIndex];
  }

  activeOrderedLayout
    .filter(
      (widget) =>
        !getLayoutWidgetRowLock(widget) &&
        widget.id !== preferredWidgetId
    )
    .forEach((widget) => {
      const nextSlot = activeOrderedLayout.findIndex(
        (unusedWidget, index) =>
          unusedWidget &&
          !normalizedSlots[index] &&
          !usedSlots.has(index + 1)
      );

      if (nextSlot === -1) {
        normalizedSlots.push(widget);
        return;
      }

      normalizedSlots[nextSlot] = widget;
      usedSlots.add(nextSlot + 1);
    });

  return [
    ...normalizedSlots.filter(Boolean),
    ...inactiveOrderedLayout,
  ].map((widget, index) => ({
    ...widget,
    order: index + 1,
  }));
}

// Esta función garantiza que el diseño del panel de control se ajuste al conjunto actual de widgets disponibles,
// aplicando valores predeterminados para cualquier entrada que falte y gestionando la migración de widgets específicos si es necesario.
function normalizeDashboardLayout(layout = [], options = {}) {
  const savedEntriesById = Array.isArray(layout)
    ? layout.reduce((entriesById, entry) => {
        if (entry?.id) {
          entriesById[entry.id] = entry;
        }

        return entriesById;
      }, {})
    : {};

  const normalizedLayout = DASHBOARD_WIDGETS.map((widget) => {
    const migratedPowerBiEntry =
      widget.id === 'powerbi-analytics'
        ? [
            savedEntriesById['nutrivisor-analytics'],
            savedEntriesById['my-smart-analytics'],
          ].filter(Boolean)
        : [];
    const savedEntry = savedEntriesById[widget.id] || {};
    const powerBiOrder = migratedPowerBiEntry.length
      ? Math.min(
          ...migratedPowerBiEntry.map((entry) =>
            Number.isFinite(Number(entry.order))
              ? Number(entry.order)
              : widget.defaultOrder
          )
        )
      : null;
    const order = Number.isFinite(Number(savedEntry.order))
      ? Number(savedEntry.order)
      : powerBiOrder || widget.defaultOrder;
    let enabled = widget.defaultEnabled;

    if (migratedPowerBiEntry.length) {
      enabled = migratedPowerBiEntry.some(
        (entry) => entry.enabled !== false
      );
    }

    if (typeof savedEntry.enabled === 'boolean') {
      enabled = savedEntry.enabled;
    }

    return {
      id: widget.id,
      enabled: widget.required ? true : enabled,
      order,
    };
  }).sort((firstWidget, secondWidget) => {
    if (firstWidget.order === secondWidget.order) {
      return (
        getRegistryOrder(firstWidget.id) -
        getRegistryOrder(secondWidget.id)
      );
    }

    return firstWidget.order - secondWidget.order;
  });

  return normalizeLockedRows(
    normalizedLayout,
    options.preferredWidgetId,
    options.preferredOrderSlot
  );
}

function persistLayout(layout, storageContext) {
  if (!canUseLocalStorage()) return;

  window.localStorage.setItem(
    getStorageKey(storageContext),
    JSON.stringify(layout)
  );
}

function readLayout(storageContext) {
  if (!canUseLocalStorage()) {
    return null;
  }

  const storedLayout = window.localStorage.getItem(
    getStorageKey(storageContext)
  );

  if (!storedLayout) {
    return null;
  }

  try {
    return JSON.parse(storedLayout);
  } catch (error) {
    return null;
  }
}

function replaceOrder(layout, orderedIds) {
  const orderById = orderedIds.reduce((result, id, index) => {
    result[id] = index + 1;
    return result;
  }, {});

  return layout.map((entry) => ({
    ...entry,
    order: orderById[entry.id] || entry.order,
  }));
}

export default {
  namespaced: true,
  state: {
    layout: [],
  },
  getters: {
    layoutById(state) {
      return state.layout.reduce((result, entry) => {
        result[entry.id] = entry;
        return result;
      }, {});
    },
  },
  actions: {
    // Esta acción carga el diseño del panel de control desde el almacenamiento local,
    // normalizándolo para garantizar que se ajuste al conjunto actual de widgets disponibles y aplicando valores predeterminados según sea necesario.
    loadDashboardLayout({ commit }, { storageContext } = {}) {
      const storedLayout = readLayout(storageContext);
      const normalizedLayout = normalizeDashboardLayout(
        storedLayout || getDefaultDashboardLayout() // El diseño predeterminado también se normaliza para garantizar que se ajuste a los widgets disponibles y tenga valores predeterminados aplicados.
      );

      commit('setDashboardLayout', normalizedLayout);
      persistLayout(normalizedLayout, storageContext);
    },
    saveDashboardLayout(
      { commit },
      {
        layout,
        storageContext,
        preferredWidgetId,
        preferredOrderSlot,
      } = {}
    ) {
      const normalizedLayout = normalizeDashboardLayout(layout, {
        preferredWidgetId,
        preferredOrderSlot,
      });

      commit('setDashboardLayout', normalizedLayout);
      persistLayout(normalizedLayout, storageContext);
    },
    normalizeDashboardLayout(
      { dispatch, state },
      { storageContext } = {}
    ) {
      dispatch('saveDashboardLayout', {
        layout: state.layout,
        storageContext,
      });
    },

    // Esta acción alterna la visibilidad de un widget específico en el diseño del panel de control,

    toggleDashboardWidget(
      { dispatch, state },
      { id, enabled, storageContext } = {}
    ) {
      const widget = getDashboardWidgetById(id); // Se verifica si el widget existe y no es obligatorio antes de permitir alternar su estado.

      if (!widget || widget.required) {
        return;
      }

      const nextLayout = state.layout.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              enabled:
                typeof enabled === 'boolean'
                  ? enabled
                  : !entry.enabled,
            }
          : entry
      );

      dispatch('saveDashboardLayout', {
        layout: nextLayout,
        storageContext,
        preferredWidgetId: id,
      });
    },
    // Estas acciones permiten mover un widget hacia arriba o hacia abajo en el orden del diseño del panel de control,
    // actualizando el orden de los widgets en consecuencia y persistiendo el nuevo diseño.
    setDashboardWidgetOrder(
      { dispatch, state },
      { id, order, storageContext } = {}
    ) {
      const widget = getDashboardWidgetById(id);
      const parsedOrder = Number(order);

      if (
        !widget ||
        widget.required ||
        !Number.isFinite(parsedOrder)
      ) {
        return;
      }

      const nextLayout = state.layout.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              order: parsedOrder,
            }
          : entry
      );

      dispatch('saveDashboardLayout', {
        layout: nextLayout,
        storageContext,
        preferredWidgetId: id,
        preferredOrderSlot: parsedOrder,
      });
    },
    moveDashboardWidgetUp(
      { dispatch, state },
      { id, orderedIds = [], storageContext } = {}
    ) {
      const currentIndex = orderedIds.indexOf(id);

      if (currentIndex <= 0) {
        return;
      }

      const nextOrderedIds = [...orderedIds];
      const previousId = nextOrderedIds[currentIndex - 1];

      nextOrderedIds[currentIndex - 1] = id;
      nextOrderedIds[currentIndex] = previousId;

      dispatch('saveDashboardLayout', {
        layout: replaceOrder(state.layout, nextOrderedIds),
        storageContext,
        preferredWidgetId: id,
      }); // El nuevo diseño se genera reemplazando el orden de los widgets según el nuevo orden de IDs y luego se guarda.
    },
    moveDashboardWidgetDown(
      { dispatch, state },
      { id, orderedIds = [], storageContext } = {}
    ) {
      const currentIndex = orderedIds.indexOf(id);

      if (
        currentIndex === -1 ||
        currentIndex >= orderedIds.length - 1
      ) {
        return;
      }

      const nextOrderedIds = [...orderedIds];
      const nextId = nextOrderedIds[currentIndex + 1];

      nextOrderedIds[currentIndex + 1] = id;
      nextOrderedIds[currentIndex] = nextId;

      dispatch('saveDashboardLayout', {
        layout: replaceOrder(state.layout, nextOrderedIds),
        storageContext,
        preferredWidgetId: id,
      });
    },
    // Esta acción restaura el diseño del panel de control a su configuración predeterminada,
    // eliminando cualquier personalización realizada por el usuario y persistiendo el diseño predeterminado.
    restoreDefaultDashboardLayout(
      { commit },
      { storageContext } = {}
    ) {
      const defaultLayout = normalizeDashboardLayout(
        getDefaultDashboardLayout()
      );

      commit('setDashboardLayout', defaultLayout);
      persistLayout(defaultLayout, storageContext);
    },
  },
  mutations: {
    setDashboardLayout(state, layout) {
      state.layout = layout;
    },
  }, // Este módulo de Vuex gestiona la personalización del panel de control, incluyendo la carga, guardado, normalización y restauración del diseño del panel de control, así como la alternancia y reordenamiento de widgets específicos.
};
